import { useTranslation } from 'react-i18next';

import { useToast } from './ui';
import { useRealtime } from '../hooks/useRealtime';
import { formatUsd } from '../lib/format';
import type { RealtimeEnvelope } from '../lib/realtime';

/**
 * Centralised translator from realtime envelopes to user-visible toasts.
 *
 * The realtime invalidator already keeps React Query in sync — this
 * component sits alongside it and gives the user *audible* feedback
 * for the events they wouldn't otherwise notice (a fee being charged,
 * a withdrawal being approved).
 *
 * Trade-resolved events are handled by `<TradeResolvedOverlay />`
 * (full-screen wow effect with confetti) so we explicitly ignore them
 * here — a tiny toast on top of the overlay is duplicative noise.
 */
export function RealtimeToasts() {
  const { t } = useTranslation();
  const toast = useToast();

  // ── user:notify ──
  useRealtime(
    (ch) => ch.endsWith(':notify'),
    (env: RealtimeEnvelope) => {
      const p = env.payload as Record<string, unknown>;
      switch (env.type) {
        case 'fee_charged': {
          const amount = formatUsd((p.amount_usd as string) ?? '0');
          toast.show(t('toast.fee_charged', { amount }), { tone: 'neutral' });
          return;
        }
        case 'withdrawal_decision': {
          const decision = p.decision as string | undefined;
          if (decision === 'approved') {
            toast.show(t('toast.withdrawal_approved'), { tone: 'success' });
          } else if (decision === 'rejected') {
            const reason = (p.reason as string | null) ?? '';
            toast.show(
              reason
                ? t('toast.withdrawal_rejected_with_reason', { reason })
                : t('toast.withdrawal_rejected'),
              { tone: 'danger' },
            );
          }
          return;
        }
        case 'config_changed': {
          // Per-user settings tweak (fee%, partner_tier, group). Quiet
          // notice — refetch already happened via invalidator.
          toast.show(t('toast.config_changed'), { tone: 'neutral' });
          return;
        }
        // bot_state and kill_switch don't need a toast — the dashboard
        // pill / top-banner update directly.
        default:
          return;
      }
    },
  );

  return null;
}
